import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import SEO from '../components/SEO.tsx';
import { CheckCircle, User, Loader } from 'lucide-react';

interface SuccessProps {
  onNavigate: (page: string) => void;
}

const Success: React.FC<SuccessProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(true);
  const currentPlan = new URLSearchParams(location.search).get('plan') || 'Premium';

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsProcessing(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  if (isProcessing) {
    return (
      <div className="min-h-screen pt-16">
        <SEO
          title="Processing Payment - 4urCloud Kexa"
          description="Processing your payment for Kexa Premium"
          keywords="payment processing, kexa premium, cloud security"
        />
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-white rounded-2xl shadow-modern p-12">
              <div className="w-16 h-16 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader className="w-8 h-8 text-white animate-spin" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-4">
                Processing Your Payment
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Please wait while we confirm your payment and update your account...
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <SEO
        title="Payment Successful - 4urCloud Kexa"
        description={`Your payment was successful! Welcome to Kexa ${currentPlan}.`}
        keywords="payment successful, kexa pro, kexa enterprise, cloud security"
      />
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl shadow-modern p-12 animate-slide-up">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              <GradientText variant="primary">Payment Successful!</GradientText>
            </h1>
            
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Thank you for upgrading to Kexa {currentPlan}! Your license has been activated and you now have access to all {currentPlan.toLowerCase()} features.
            </p>


            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => onNavigate('account')}
                variant="primary"
                size="lg"
                leftIcon={<User size={20} />}
              >
                View Account & License
              </Button>
              <Button
                onClick={() => onNavigate('deploy')}
                variant="outline"
                size="lg"
              >
                Deploy Kexa
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Success;